import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBan,
  FaCheckCircle,
  FaDownload,
  FaEdit,
  FaFileImport,
  FaHistory,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import api from "../api";
import {
  downloadMarketingContactsTemplate,
  exportMarketingContacts,
  readMarketingContactsFile,
} from "../utils/marketingEmailContactsSpreadsheet";

const emptyForm = () => ({
  id: null,
  email: "",
  name: "",
  company: "",
  segment: "",
  phone: "",
  notes: "",
  active: true,
});

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("pt-BR");
};

const statusLabel = (delivery, suppression) => {
  if (suppression) return "Cancelou recebimento";
  if (!delivery) return "Sem envio";
  if (delivery.status === "OPENED") return "Aberto";
  if (delivery.status === "SENT") return "Entregue (SMTP)";
  if (delivery.status === "FAILED") return "Voltou/Falhou";
  if (delivery.status === "SUPPRESSED") return "Cancelado";
  return delivery.status || "Pendente";
};

const statusClass = (label) => {
  if (label === "Aberto") return "bg-emerald-50 text-emerald-700";
  if (label === "Entregue (SMTP)") return "bg-blue-50 text-blue-700";
  if (label === "Voltou/Falhou") return "bg-rose-50 text-rose-700";
  if (label === "Cancelou recebimento" || label === "Cancelado") return "bg-slate-100 text-slate-600";
  return "bg-amber-50 text-amber-700";
};


const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  (error?.request ? "O servidor não respondeu à solicitação." : "") ||
  error?.message ||
  fallback;

export default function CRMEmailMarketingContacts({
  contacts = [],
  deliveries = [],
  suppressions = [],
  onReload,
}) {
  const fileRef = useRef(null);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [editorOpen, setEditorOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const [sourceSummary, setSourceSummary] = useState([]);
  const [sourceConfig, setSourceConfig] = useState([]);
  const [sourceSelections, setSourceSelections] = useState({});
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [syncPanelOpen, setSyncPanelOpen] = useState(false);

  const suppressionMap = useMemo(
    () => new Map(suppressions.map((item) => [String(item.email || "").toLowerCase(), item])),
    [suppressions],
  );

  const lastDeliveryMap = useMemo(() => {
    const map = new Map();
    deliveries.forEach((item) => {
      const email = String(item.email || "").toLowerCase();
      if (email && !map.has(email)) map.set(email, item);
    });
    return map;
  }, [deliveries]);

  const rows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("pt-BR");
    return contacts
      .map((item) => {
        const email = String(item.email || "").toLowerCase();
        const lastDelivery = lastDeliveryMap.get(email);
        const suppression = suppressionMap.get(email);
        return {
          ...item,
          lastDelivery,
          suppression,
          deliveryLabel: statusLabel(lastDelivery, suppression),
          lastDeliveryAt: formatDateTime(lastDelivery?.openedAt || lastDelivery?.sentAt || lastDelivery?.createdAt),
        };
      })
      .filter((item) => {
        if (!term) return true;
        return [item.email, item.name, item.company, item.segment, item.phone, item.deliveryLabel]
          .join(" ")
          .toLocaleLowerCase("pt-BR")
          .includes(term);
      });
  }, [contacts, lastDeliveryMap, suppressionMap, query]);

  const loadSourceConfig = useCallback(async () => {
    try {
      const response = await api.get("/mail/marketing/contact-sources", {
        skipTransientRetry: true,
      });
      const sources = Array.isArray(response.data?.sources) ? response.data.sources : [];
      setSourceConfig(sources);
      setSourceSelections((current) => {
        const next = { ...current };
        sources.forEach((source) => {
          const availableStatuses = new Set((source.statuses || []).map((item) => String(item.value)));
          const defaults = (source.defaultStatuses || []).filter((value) => availableStatuses.has(String(value)));
          const previous = next[source.key];
          const previousStatuses = Array.isArray(previous?.statuses)
            ? previous.statuses.filter((value) => availableStatuses.has(String(value)))
            : [];
          const processIds = source.key === "PROCESS"
            ? (
                Array.isArray(previous?.processIds) && previous.processIds.length
                  ? previous.processIds
                  : (source.processes || [])
                      .filter((process) => process.status === "published")
                      .map((process) => process.id)
              )
            : [];
          next[source.key] = {
            enabled: previous?.enabled !== false && (source.statuses || []).length > 0,
            statuses: previousStatuses.length
              ? previousStatuses
              : (defaults.length ? defaults : (source.statuses || []).slice(0, 1).map((item) => item.value)),
            processIds,
          };
        });
        return next;
      });
    } catch (error) {
      setFeedback(apiErrorMessage(error, "Não foi possível carregar os módulos disponíveis."));
      setSourceConfig([]);
    } finally {
      setSourcesLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!sourcesLoaded) void loadSourceConfig();
  }, [loadSourceConfig, sourcesLoaded]);

  const updateSourceSelection = (key, updater) => {
    setSourceSelections((current) => ({
      ...current,
      [key]: updater(current[key] || { enabled: true, statuses: [], processIds: [] }),
    }));
  };

  const toggleSource = (key) => {
    updateSourceSelection(key, (current) => ({ ...current, enabled: !current.enabled }));
  };

  const toggleSourceStatus = (key, status) => {
    updateSourceSelection(key, (current) => {
      const statuses = Array.isArray(current.statuses) ? current.statuses : [];
      const exists = statuses.includes(status);
      return {
        ...current,
        statuses: exists ? statuses.filter((item) => item !== status) : [...statuses, status],
      };
    });
  };

  const toggleProcess = (key, processId) => {
    updateSourceSelection(key, (current) => {
      const processIds = Array.isArray(current.processIds) ? current.processIds : [];
      const exists = processIds.includes(processId);
      return {
        ...current,
        processIds: exists ? processIds.filter((item) => item !== processId) : [...processIds, processId],
      };
    });
  };

  const syncConnectedSources = useCallback(async () => {
    setBusy("sources");
    setFeedback("");
    try {
      const selections = sourceConfig
        .filter((source) => sourceSelections[source.key]?.enabled)
        .map((source) => ({
          key: source.key,
          statuses: sourceSelections[source.key]?.statuses || [],
          processIds: source.key === "PROCESS" ? sourceSelections[source.key]?.processIds || [] : [],
        }))
        .filter((selection) => selection.statuses.length > 0);

      if (!selections.length) {
        setFeedback("Selecione ao menos um módulo e um status para sincronizar.");
        return;
      }

      const response = await api.post("/mail/marketing/contacts/sync", { selections });
      const summary = Array.isArray(response.data?.summary) ? response.data.summary : [];
      setSourceSummary(summary);
      const found = Number(response.data?.contacts?.length || 0);
      const created = Number(response.data?.created || 0);
      const updated = Number(response.data?.updated || 0);
      const skipped = Number(response.data?.skipped || 0);
      setFeedback(
        found
          ? `${found} e-mail(s) localizado(s) · ${created} novo(s) · ${updated} atualizado(s)${skipped ? ` · ${skipped} ignorado(s)` : ""}.`
          : "Nenhum e-mail foi encontrado com os módulos e status selecionados.",
      );
      await onReload?.();
      await loadSourceConfig();
    } catch (error) {
      setFeedback(apiErrorMessage(error, "Não foi possível sincronizar os módulos."));
    } finally {
      setBusy("");
    }
  }, [loadSourceConfig, onReload, sourceConfig, sourceSelections]);

  const openNew = () => {
    setForm(emptyForm());
    setEditorOpen(true);
    setFeedback("");
  };

  const edit = (item) => {
    setForm({
      id: item.id,
      email: item.email || "",
      name: item.name || "",
      company: item.company || "",
      segment: item.segment || "",
      phone: item.phone || "",
      notes: item.notes || "",
      active: item.active !== false,
    });
    setEditorOpen(true);
    setFeedback("");
  };

  const save = async () => {
    if (!form.email.trim()) return setFeedback("Informe o e-mail.");
    setBusy("save");
    setFeedback("");
    try {
      if (form.id) await api.put("/mail/marketing/contacts/" + form.id, form);
      else await api.post("/mail/marketing/contacts", form);
      setEditorOpen(false);
      setForm(emptyForm());
      setFeedback("Cadastro salvo com sucesso.");
      await onReload?.();
    } catch (error) {
      setFeedback(apiErrorMessage(error, "Não foi possível salvar o cadastro."));
    } finally {
      setBusy("");
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Excluir o cadastro de " + item.email + "?")) return;
    setBusy("delete-" + item.id);
    try {
      await api.delete("/mail/marketing/contacts/" + item.id);
      setFeedback("Cadastro excluído.");
      await onReload?.();
    } catch (error) {
      setFeedback(apiErrorMessage(error, "Não foi possível excluir o cadastro."));
    } finally {
      setBusy("");
    }
  };

  const showHistory = async (item) => {
    setBusy("history-" + item.id);
    setFeedback("");
    try {
      const response = await api.get("/mail/marketing/contacts/" + item.id + "/history");
      setHistory(response.data);
    } catch (error) {
      setFeedback(apiErrorMessage(error, "Não foi possível carregar o histórico."));
    } finally {
      setBusy("");
    }
  };

  const importFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy("import");
    setFeedback("");
    try {
      const rowsToImport = await readMarketingContactsFile(file);
      if (!rowsToImport.length) throw new Error("Nenhum e-mail encontrado na planilha.");
      const response = await api.post("/mail/marketing/contacts/import", { rows: rowsToImport });
      setFeedback(
        String(response.data?.created || 0) + " criado(s) · " +
        String(response.data?.updated || 0) + " atualizado(s) · " +
        String(response.data?.skipped || 0) + " ignorado(s)",
      );
      await onReload?.();
    } catch (error) {
      setFeedback(apiErrorMessage(error, "Não foi possível importar a planilha."));
    } finally {
      setBusy("");
    }
  };

  const exportRows = () => exportMarketingContacts(
    contacts.map((item) => {
      const email = String(item.email || "").toLowerCase();
      const lastDelivery = lastDeliveryMap.get(email);
      const suppression = suppressionMap.get(email);
      return {
        ...item,
        deliveryLabel: statusLabel(lastDelivery, suppression),
        lastDeliveryAt: formatDateTime(lastDelivery?.openedAt || lastDelivery?.sentAt || lastDelivery?.createdAt),
      };
    }),
  );

  return (
    <section className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="space-y-4 rounded-2xl bg-gradient-to-r from-slate-50 via-white to-blue-50/70 p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-600 text-[11px] font-black tracking-wide text-white shadow-sm">CRM</span>
            <div>
              <h3 className="text-lg font-black text-slate-900">Cadastros de e-mail</h3>
              <p className="mt-0.5 text-xs font-medium text-slate-500">Centralize os destinatários das campanhas em um só lugar.</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-extrabold text-slate-600 shadow-sm">{contacts.length} cadastrado(s)</span>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-extrabold text-emerald-700">{contacts.filter((item) => item.active !== false).length} ativo(s)</span>
              </div>
            </div>
          </div>

          <button type="button" onClick={openNew} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-xs font-extrabold text-white shadow-sm transition hover:bg-blue-700">
            <FaPlus /> Novo e-mail
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3">
          <button
            type="button"
            onClick={() => setSyncPanelOpen((current) => !current)}
            className={`inline-flex items-center gap-2 rounded-xl border px-3.5 py-2 text-xs font-extrabold transition ${syncPanelOpen ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:border-blue-200 hover:text-blue-700"}`}
          >
            <FaSyncAlt /> {syncPanelOpen ? "Fechar sincronização" : "Trazer e-mails dos módulos"}
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={downloadMarketingContactsTemplate} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-extrabold text-slate-600 hover:bg-slate-50">
              <FaDownload className="mr-1 inline" /> Modelo
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy === "import"} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-extrabold text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              <FaFileImport className="mr-1 inline" /> {busy === "import" ? "Importando..." : "Importar"}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={importFile} />
            <button type="button" onClick={exportRows} className="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-extrabold text-slate-600 hover:bg-slate-50">
              <FaDownload className="mr-1 inline" /> Exportar
            </button>
          </div>
        </div>

        {syncPanelOpen && (
          <div className="rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h4 className="text-sm font-extrabold text-slate-800">Fontes da sincronização</h4>
                <p className="mt-0.5 text-xs text-slate-500">Ative somente os módulos desejados e marque os status que devem entrar no cadastro de e-mail.</p>
              </div>
              <button
                type="button"
                onClick={syncConnectedSources}
                disabled={busy === "sources" || !sourceConfig.length}
                className="inline-flex items-center gap-2 rounded-xl bg-[#0b3474] px-4 py-2.5 text-xs font-extrabold text-white shadow-sm transition hover:bg-[#0a2c62] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaSyncAlt className={busy === "sources" ? "animate-spin" : ""} />
                {busy === "sources" ? "Sincronizando..." : "Sincronizar selecionados"}
              </button>
            </div>

            {!sourcesLoaded ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-5 text-center text-sm font-semibold text-slate-400">Carregando módulos...</div>
            ) : sourceConfig.length ? (
              <div className="grid gap-3 lg:grid-cols-2">
                {sourceConfig.map((source) => {
                  const selection = sourceSelections[source.key] || { enabled: false, statuses: [], processIds: [] };
                  const enabled = Boolean(selection.enabled);
                  return (
                    <article key={source.key} className={`rounded-2xl border p-4 transition ${enabled ? "border-blue-200 bg-blue-50/35" : "border-slate-200 bg-slate-50/60"}`}>
                      <div className="flex items-start justify-between gap-3">
                        <button type="button" onClick={() => toggleSource(source.key)} className="flex min-w-0 items-start gap-3 text-left">
                          <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[10px] font-black ${enabled ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-transparent"}`}>✓</span>
                          <span className="min-w-0">
                            <strong className="block text-sm text-slate-800">{source.label}</strong>
                            <span className="mt-0.5 block text-xs leading-relaxed text-slate-500">{source.description}</span>
                          </span>
                        </button>
                        {sourceSummary.find((item) => item.key === source.key) && (
                          <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[10px] font-bold text-blue-700 shadow-sm">
                            {sourceSummary.find((item) => item.key === source.key)?.found || 0} encontrado(s)
                          </span>
                        )}
                      </div>

                      <div className="mt-3">
                        <p className="mb-2 text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Status incluídos</p>
                        <div className="flex flex-wrap gap-2">
                          {(source.statuses || []).map((status) => {
                            const selected = enabled && (selection.statuses || []).includes(status.value);
                            return (
                              <button
                                key={status.value}
                                type="button"
                                disabled={!enabled}
                                onClick={() => toggleSourceStatus(source.key, status.value)}
                                className={`rounded-full border px-3 py-1.5 text-[11px] font-bold transition disabled:cursor-not-allowed disabled:opacity-45 ${selected ? "border-blue-300 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-200"}`}
                              >
                                {status.label} <span className={selected ? "text-blue-100" : "text-slate-400"}>({status.count})</span>
                              </button>
                            );
                          })}
                          {!(source.statuses || []).length && <span className="text-xs text-slate-400">Nenhum registro disponível.</span>}
                        </div>
                      </div>

                      {source.key === "PROCESS" && enabled && (
                        <div className="mt-4 rounded-xl border border-slate-200 bg-white p-3">
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <p className="text-[10px] font-extrabold uppercase tracking-wide text-slate-400">Processos incluídos</p>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={() => updateSourceSelection(source.key, (current) => ({ ...current, processIds: (source.processes || []).map((process) => process.id) }))}
                                className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-500"
                              >
                                Todos
                              </button>
                              <button
                                type="button"
                                onClick={() => updateSourceSelection(source.key, (current) => ({ ...current, processIds: [] }))}
                                className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-500"
                              >
                                Limpar
                              </button>
                            </div>
                          </div>
                          <div className="max-h-36 space-y-1 overflow-auto pr-1">
                            {(source.processes || []).map((process) => {
                              const selected = (selection.processIds || []).includes(process.id);
                              return (
                                <label key={process.id} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-50">
                                  <span className="flex min-w-0 items-center gap-2">
                                    <input type="checkbox" checked={selected} onChange={() => toggleProcess(source.key, process.id)} className="h-4 w-4 rounded border-slate-300 text-blue-600" />
                                    <span className="truncate">{process.name}</span>
                                  </span>
                                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold ${process.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{process.statusLabel}</span>
                                </label>
                              );
                            })}
                            {!(source.processes || []).length && <p className="py-2 text-center text-xs text-slate-400">Nenhum processo cadastrado.</p>}
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 p-5 text-center text-sm font-semibold text-slate-400">Nenhum módulo disponível para sincronização.</div>
            )}
          </div>
        )}
      </div>

      {feedback && (
        <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700">
          {feedback}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-lg">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar por nome, e-mail, empresa ou status" className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-50" />
        </div>
        <span className="text-xs font-semibold text-slate-400">{rows.length} resultado(s)</span>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Contato</th>
              <th className="px-4 py-3">Empresa/Segmento</th>
              <th className="px-4 py-3">Situação</th>
              <th className="px-4 py-3">Último envio</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((item) => (
              <tr key={item.id} className="transition hover:bg-slate-50/80">
                <td className="px-4 py-3">
                  <strong className="block text-slate-800">{item.name || item.email}</strong>
                  <span className="text-xs text-slate-500">{item.email}</span>
                  {item.active === false && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">Inativo</span>}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <span className="block">{item.company || "—"}</span>
                  <span className="text-xs text-slate-400">{item.segment || "Sem segmento"}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={"rounded-full px-2.5 py-1 text-xs font-extrabold " + statusClass(item.deliveryLabel)}>{item.deliveryLabel}</span>
                  {item.suppression && <FaBan className="ml-2 inline text-rose-500" title="O destinatário cancelou o recebimento." />}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-500">{item.lastDeliveryAt}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5">
                    <button type="button" onClick={() => showHistory(item)} disabled={busy === "history-" + item.id} className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-bold text-slate-600"><FaHistory /></button>
                    <button type="button" onClick={() => edit(item)} className="rounded-lg border border-blue-200 px-2.5 py-1.5 text-xs font-bold text-blue-600"><FaEdit /></button>
                    <button type="button" onClick={() => remove(item)} disabled={busy === "delete-" + item.id} className="rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs font-bold text-rose-600"><FaTrash /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">Nenhum cadastro de e-mail encontrado.</td></tr>}
          </tbody>
        </table>
      </div>

      {editorOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onMouseDown={() => setEditorOpen(false)}>
          <div className="w-full max-w-2xl rounded-3xl bg-white p-5 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div><h3 className="text-lg font-black text-slate-900">{form.id ? "Editar e-mail" : "Novo e-mail"}</h3><p className="text-xs text-slate-500">Mini cadastro do destinatário de campanhas.</p></div>
              <button type="button" onClick={() => setEditorOpen(false)} className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500"><FaTimes /></button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <label className="text-xs font-bold text-slate-500">E-mail *<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800" /></label>
              <label className="text-xs font-bold text-slate-500">Nome<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800" /></label>
              <label className="text-xs font-bold text-slate-500">Empresa<input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800" /></label>
              <label className="text-xs font-bold text-slate-500">Segmento<input value={form.segment} onChange={(e) => setForm({ ...form, segment: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800" /></label>
              <label className="text-xs font-bold text-slate-500">Telefone<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800" /></label>
              <label className="flex items-end gap-2 pb-2 text-sm font-bold text-slate-600"><input type="checkbox" checked={form.active !== false} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Cadastro ativo</label>
              <label className="text-xs font-bold text-slate-500 md:col-span-2">Observações<textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800" /></label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setEditorOpen(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-slate-600">Cancelar</button>
              <button type="button" onClick={save} disabled={busy === "save"} className="rounded-full bg-blue-600 px-5 py-2 text-sm font-extrabold text-white disabled:opacity-50">
                {busy === "save" ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {history && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-sm" onMouseDown={() => setHistory(null)}>
          <div className="max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="mb-4 flex items-center justify-between">
              <div><h3 className="text-lg font-black text-slate-900">Histórico de {history.contact?.email}</h3><p className="text-xs text-slate-500">Envios, aberturas, falhas e cancelamento de recebimento.</p></div>
              <button type="button" onClick={() => setHistory(null)} className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 text-slate-500"><FaTimes /></button>
            </div>
            {history.suppression && (
              <div className="mb-4 flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-bold text-rose-700">
                <FaBan /> Recebimento cancelado em {formatDateTime(history.suppression.unsubscribedAt)}
              </div>
            )}
            <div className="space-y-2">
              {(history.deliveries || []).map((item) => {
                const label = statusLabel(item, null);
                return (
                  <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3">
                    <div className="min-w-0"><strong className="block truncate text-sm text-slate-800">{item.subject}</strong><span className="text-xs text-slate-400">{formatDateTime(item.openedAt || item.sentAt || item.createdAt)}</span></div>
                    <div className="flex items-center gap-2"><span className={"rounded-full px-2.5 py-1 text-xs font-extrabold " + statusClass(label)}>{label}</span>{item.error && <span className="max-w-xs text-xs text-rose-600">{item.error}</span>}</div>
                  </div>
                );
              })}
              {!(history.deliveries || []).length && <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">Nenhum envio para este e-mail.</div>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
